import { MouseEvent } from 'react';
import styled from '@emotion/styled';
import { Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { MessageCircle } from 'icons';
import { palette } from 'utils';
import { DocumentComment, DocumentCommentSource } from 'types';

export interface DocumentCommentIndicatorProps {
  /** Total number of comments on the document (from the list endpoint). */
  commentsCount: number;
  /** Up to 3 newest comments, newest first (from the list endpoint). */
  lastComments?: DocumentComment[];
  /** Fired when the icon is clicked (wired to the comments modal in Part 2). */
  onClick?: (event: MouseEvent) => void;
  /**
   * Optional popup-container resolver. Pass a function returning the table's
   * scroll container when the default body-anchored tooltip gets clipped near
   * the container edge.
   */
  getPopupContainer?: (triggerNode: HTMLElement) => HTMLElement;
}

const PREVIEW_LIMIT = 3;

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  /* Match the "Procesat"/done status colour; the icon path uses fill="currentColor",
     so the whole glyph renders filled in this green. */
  color: ${palette['green-100']};
  font-size: 1.6rem;
  line-height: 1;

  &:hover {
    color: ${palette['green-125']};
  }
`;

const TooltipContent = styled.div`
  max-width: 240px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const CommentPreviewRow = styled.div`
  display: flex;
  flex-direction: column;
`;

const CommentAuthor = styled.span`
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CommentText = styled.span`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
`;

const MoreLine = styled.span`
  opacity: 0.75;
  font-style: italic;
`;

const commentAuthorLabel = (comment: DocumentComment): string =>
  comment.source === DocumentCommentSource.APOLLO
    ? comment.author || comment.source
    : comment.user?.name || '';

export const DocumentCommentIndicator = ({
  commentsCount,
  lastComments = [],
  onClick,
  getPopupContainer
}: DocumentCommentIndicatorProps) => {
  const { t } = useTranslation();

  // No signal to show when the document has no comments.
  if (!commentsCount || commentsCount <= 0) {
    return null;
  }

  const preview = lastComments.slice(0, PREVIEW_LIMIT);
  const remaining = commentsCount - preview.length;

  const title = (
    <TooltipContent>
      {preview.map((comment) => (
        <CommentPreviewRow key={comment.id}>
          <CommentAuthor>{commentAuthorLabel(comment)}</CommentAuthor>
          <CommentText>{comment.text}</CommentText>
        </CommentPreviewRow>
      ))}
      {remaining > 0 && <MoreLine>{t('commentsMore', { count: remaining })}</MoreLine>}
    </TooltipContent>
  );

  const handleClick = (event: MouseEvent) => {
    // Keep the row's navigation Link from firing; this opens the comments modal.
    event.preventDefault();
    event.stopPropagation();
    onClick?.(event);
  };

  return (
    <Tooltip overlayClassName="sm-tooltip" title={title} getPopupContainer={getPopupContainer}>
      <IconButton type="button" onClick={handleClick} aria-label={t('message')}>
        <MessageCircle />
      </IconButton>
    </Tooltip>
  );
};
